<template>
  <div class="match-finish-container">
    <h4 class="text-center mt-2 mb-0"><font-awesome-icon :icon="['fas', 'flag-checkered']" /> 試合結果編集スキャン</h4>
    <div class="scanner-section">
      <h5 class="text-center mb-3">QRコードをスキャンしてください</h5>
      <div id="qr-reader" class="qr-reader"></div>

      <div v-if="scanError" class="alert alert-danger mt-3 mx-3">{{ scanError }}</div>
      <div v-if="!cameraAvailable && cameraChecked" class="alert alert-warning mt-3 mx-3">
        <font-awesome-icon :icon="['fas', 'camera']" /> カメラを起動できませんでした。<br>
        HTTPS環境またはlocalhostでアクセスしてください。
      </div>
    </div>

    <div class="modal fade" id="editMatchModal" tabindex="-1" aria-labelledby="editMatchModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editMatchModalLabel">試合結果編集</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <EditMatchScoreFormComponent :match="matchData" :matchKey="matchKey" v-if="matchData" />
          </div>
        </div>
      </div>
    </div>

    <ScanHistoryComponent
      :entries="currentEntries"
      :availableDates="availableDates"
      :selectedDate="selectedDate"
      @update:selectedDate="selectedDate = $event"
    />

    <FooterComponent />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { Modal } from 'bootstrap'
import { Html5Qrcode } from 'html5-qrcode'
import { ref as fbRef, getDatabase, get, child } from 'firebase/database'
import EditMatchScoreFormComponent from '@/components/EditMatchScoreFormComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import ScanHistoryComponent from '@/components/ScanHistoryComponent.vue'
import { useScanHistory } from '@/composables/useScanHistory'

const db = getDatabase()
const matchData = ref(null)
const matchKey = ref('')
const scanError = ref('')
const cameraAvailable = ref(false)
const { selectedDate, availableDates, currentEntries, addEntry } = useScanHistory('scanHistory_finish')
const cameraChecked = ref(false)
let html5Qrcode = null
let editModal = null
const successSound = new Audio('/sound/success1.mp3')
const errorSound = new Audio('/sound/error1.mp3')

const extractMatchKey = (text) => {
  const match = text.match(/\/match\/([A-Za-z0-9]+-[A-Za-z0-9]+)/)
  return match ? match[1] : null
}

let processing = false

const loadMatch = async (rawText) => {
  if (!rawText || processing) return
  processing = true
  const key = extractMatchKey(rawText)
  if (!key) {
    scanError.value = 'QRコードの形式が正しくありません'
    addEntry('(Path error)', 'error')
    errorSound.play().catch(() => {})
    setTimeout(() => { processing = false }, 800)
    return
  }
  scanError.value = ''
  matchKey.value = key

  try {
    const snapshot = await get(child(fbRef(db), `/2026-summer/matches/${key}`))
    if (snapshot.exists()) {
      matchData.value = snapshot.val()
      addEntry(key, 'success')
      successSound.play().catch(() => {})
      await stopScanner()
      nextTick(() => {
        if (!editModal) {
          editModal = new Modal(document.getElementById('editMatchModal'))
          document.getElementById('editMatchModal').addEventListener('hidden.bs.modal', onModalClosed)
        }
        editModal.show()
      })
      setTimeout(() => { processing = false }, 800)
    } else {
      scanError.value = `試合が見つかりません: ${key}`
      addEntry(key, 'error')
      errorSound.play().catch(() => {})
      setTimeout(() => { processing = false }, 800)
    }
  } catch (e) {
    scanError.value = `データ取得エラー: ${e.message}`
    addEntry(key, 'error')
    errorSound.play().catch(() => {})
    setTimeout(() => { processing = false }, 800)
  }
}

const onScanSuccess = async (decodedText) => {
  await loadMatch(decodedText)
}

const startScanner = async () => {
  matchData.value = null
  try {
    if (!html5Qrcode) {
      html5Qrcode = new Html5Qrcode('qr-reader')
    }
    await html5Qrcode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 175, height: 175 } },
      onScanSuccess
    )
    cameraAvailable.value = true
  } catch (e) {
    cameraAvailable.value = false
  }
  cameraChecked.value = true
}

const stopScanner = async () => {
  if (html5Qrcode) {
    try {
      await html5Qrcode.stop()
    } catch (e) {
      // already stopped
    }
  }
}

const onModalClosed = () => {
  matchData.value = null
  matchKey.value = ''
  scanError.value = ''
  cameraChecked.value = false
  setTimeout(() => startScanner(), 100)
}

onMounted(() => {
  nextTick(() => {
    setTimeout(() => startScanner(), 300)
  })
})

onBeforeUnmount(async () => {
  await stopScanner()
})
</script>

<style scoped>
.match-finish-container {
  min-height: 100vh;
  padding-bottom: 80px;
}

.scanner-section {
  padding-top: 20px;
}

.qr-reader {
  max-width: 350px;
  margin: 0 auto;
}
</style>
